/**
 * Shared constants and helpers for the technician installation photos UI.
 *
 * The 4-photo limit is enforced in the FRONTEND ONLY (per the requirements /
 * architecture note). Keeping it here in one place makes it trivial to raise
 * later without any backend change.
 */

// Frontend-only cap on the number of photos per device.
export const MAX_PHOTOS = 4;

// Generous max file size for UHD photos. Authoritative check also lives on the
// backend; this is the client-side pre-check.
export const MAX_FILE_SIZE_MB = 30;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Supported image formats.
export const SUPPORTED_MIME_TYPES = ["image/jpeg", "image/png"];
export const SUPPORTED_EXTENSIONS = ["jpg", "jpeg", "png"];
export const FILE_INPUT_ACCEPT = "image/jpeg,image/png";

/**
 * Strip the `data:<mime>;base64,` prefix from a data URL, returning just the
 * base64 payload (what the backend expects in `encodedImage`).
 */
export const stripDataUrlPrefix = (dataUrl) => {
  if (typeof dataUrl !== "string") return "";
  const commaIndex = dataUrl.indexOf(",");
  return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
};

/**
 * Derive a lowercase extension from a MIME type (jpeg -> jpg normalization is
 * intentionally NOT done here; backend accepts jpg/jpeg/png).
 */
export const extensionFromMimeType = (mimeType) => {
  switch (mimeType) {
    case "image/jpeg":
      return "jpeg";
    case "image/png":
      return "png";
    default:
      return "";
  }
};

/**
 * Read a File/Blob as a base64 data URL.
 */
export const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Validate a selected file against the supported formats and max size.
 * Returns { valid: boolean, error?: string }.
 */
export const validateFile = (file) => {
  if (!file) {
    return { valid: false, error: "No file selected." };
  }
  if (!SUPPORTED_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: "Unsupported file type. Please choose a JPG or PNG image.",
    };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`,
    };
  }
  return { valid: true };
};
